import React, { useState, useMemo } from 'react';
import { useLanguageNavigation } from '../components/routing/LanguageWrapper';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { ROLES } from '../utils/rbac';
import { MOCK_REPORTS, addMockReport, getReportsKPIs, generateDefaultModules } from '../utils/mockReportsData';
import { setStoredMaxStepIndex } from '../utils/stepperProgress';
import { ReportsKPICards } from '../components/reports/ReportsHub/ReportsKPICards';
import { ReportsFilters } from '../components/reports/ReportsHub/ReportsFilters';
import { ReportsTable } from '../components/reports/ReportsHub/ReportsTable';
import { CreateReportModal } from '../components/reports/ReportsHub/CreateReportModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import './ReportsHubPage.css';

export function ReportsHubPage() {
  const { t } = useTranslation();
  const { navigateTo } = useLanguageNavigation();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [reports, setReports] = useState(MOCK_REPORTS);
  const [deletingReport, setDeletingReport] = useState(null);
  const [loading, setLoading] = useState(false);

  // RBAC permissions
  const canCreate = [ROLES.ADMIN, ROLES.REVIEWER].includes(user?.role);
  const canEdit = [ROLES.ADMIN, ROLES.REVIEWER].includes(user?.role);
  const canDelete = user?.role === ROLES.ADMIN || user?.role === ROLES.SUPERADMIN;

  // Calculate KPIs from all reports
  const kpis = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const currentYearReports = reports.filter(r => r.year === currentYear);
    
    return {
      totalReports: reports.length,
      reportsInProgress: reports.filter(r => ['draft', 'in_progress', 'needs_review'].includes(r.status)).length,
      completedReports: currentYearReports.filter(r => r.status === 'completed').length,
    };
  }, [reports]);

  // Filter reports
  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      // Search filter
      const matchesSearch = !searchQuery || 
        report.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        report.year.toString().includes(searchQuery);
      
      // Status filter
      const matchesStatus = statusFilter === 'all' || 
        report.status === statusFilter;
      
      // Year filter
      const matchesYear = yearFilter === 'all' || 
        report.year === parseInt(yearFilter);
      
      return matchesSearch && matchesStatus && matchesYear;
    });
  }, [reports, searchQuery, statusFilter, yearFilter]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setYearFilter('all');
  };

  const handleCreateReport = async (formData) => {
    const reportTitleByType = {
      relazione_agronomica_primo_anno: t('reports.types.relazione_agronomica_primo_anno'),
      relazione_agronomica_annuale: t('reports.types.relazione_agronomica_annuale'),
      plv_drop_report: t('reports.types.plv_drop_report'),
    };
    const reportTitle = reportTitleByType[formData.type] || formData.name;
    const newReport = {
      id: `report-${Date.now()}`,
      name: formData.name,
      year: formData.year,
      type: formData.type,
      reportType: formData.type, // For backward compatibility
      status: 'draft',
      progress: 0,
      lastModified: new Date(),
      createdBy: user?.email || 'unknown@example.com',
      createdAt: new Date(),
      generatedBy: user?.email || 'unknown@example.com',
      generatedAt: new Date(),
      modules: generateDefaultModules(formData.type),
      documents: [],
      process: 'configurator',
      // Additional fields for compatibility
      title: reportTitle,
      subtitle: '',
      fileUrl: null,
      fileSize: 0,
      documentCount: 0,
      stationsCovered: 0,
      pages: 0,
    };

    addMockReport(newReport);
    setReports([...MOCK_REPORTS]);
    setStoredMaxStepIndex(newReport.id, 0);
    
    toast({
      title: t('common.success'),
      description: t('reports.hub.toast.created'),
      variant: 'success',
    });

    // Navigate to workspace
    navigateTo(`/reports/${newReport.id}`);
  };

  const handleDuplicate = (report) => {
    const duplicatedReport = {
      ...report,
      id: `report-${Date.now()}`,
      name: `${report.name} (Copy)`,
      status: 'draft',
      progress: 0,
      lastModified: new Date(),
      createdAt: new Date(),
      modules: generateDefaultModules(report.type),
    };

    addMockReport(duplicatedReport);
    setReports([...MOCK_REPORTS]);
    
    toast({
      title: t('common.success'),
      description: t('reports.hub.toast.duplicated'),
      variant: 'success',
    });

    navigateTo(`/reports/${duplicatedReport.id}`);
  };

  const handleDelete = (report) => {
    setDeletingReport(report);
  };

  const confirmDelete = () => {
    if (deletingReport) {
      const reportIndex = MOCK_REPORTS.findIndex(r => r.id === deletingReport.id);
      if (reportIndex >= 0) {
        MOCK_REPORTS.splice(reportIndex, 1);
      }
      setReports([...MOCK_REPORTS]);
      
      toast({
        title: t('common.success'),
        description: t('reports.hub.toast.deleted'),
        variant: 'success',
      });
      
      setDeletingReport(null);
    }
  };

  return (
    <div className="reports-hub-page">
      <div className="reports-hub-page__container">
        {/* Header */}
        <div className="reports-hub-page__header">
          <div className="reports-hub-page__header-left">
            <h2 className="reports-hub-page__title">{t('reports.title')}</h2>
            <p className="reports-hub-page__subtitle">
              {t('reports.hub.subtitle')}
            </p>
          </div>
          {canCreate && (
            <Button
              type="button"
              variant="primary"
              icon={<Plus size={18} />}
              onClick={() => setIsCreateModalOpen(true)}
            >
              {t('reports.hub.actions.createNew')}
            </Button>
          )}
        </div>

        {/* KPI Cards */}
        <ReportsKPICards kpis={kpis} />

        {/* Filters */}
        <ReportsFilters
          searchQuery={searchQuery}
          statusFilter={statusFilter}
          yearFilter={yearFilter}
          onSearchChange={setSearchQuery}
          onStatusChange={setStatusFilter}
          onYearChange={setYearFilter}
          onClearFilters={handleClearFilters}
        />

        {/* Table */}
        <ReportsTable
          reports={filteredReports}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          loading={loading}
          onCreateNew={() => setIsCreateModalOpen(true)}
        />

        {/* Create Modal */}
        <CreateReportModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateReport}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deletingReport} onOpenChange={(open) => !open && setDeletingReport(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('reports.hub.deleteDialog.title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('reports.hub.deleteDialog.message', { name: deletingReport?.name })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeletingReport(null)}>
                {t('common.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="alert-dialog-action--danger"
              >
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

export default ReportsHubPage;
